// ABOUTME: custom theme provider that dynamically applies tenant branding colors with dark mode support
// ABOUTME: creates material-ui theme with tenant primary color, intelligent shade generation, and dark mode variants

import { CssBaseline } from "@mui/material";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getSystemPrefersDark,
  ThemeContext,
  type ThemeMode,
} from "../hooks/useAppTheme";
import { setCSSVariables } from "../utils/cssVariables";

// utility function to generate color shades from a base color
const generateColorPalette = (baseColor: string) => {
  // convert hex to rgb
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 107, g: 114, b: 128 }; // fallback to neutral gray
  };

  // generate lighter/darker variants
  const adjustBrightness = (
    color: { r: number; g: number; b: number },
    factor: number,
  ) => {
    const adjust = (channel: number) => {
      const adjusted = Math.round(channel * factor);
      return Math.min(255, Math.max(0, adjusted));
    };

    return {
      r: adjust(color.r),
      g: adjust(color.g),
      b: adjust(color.b),
    };
  };

  const rgbToHex = (color: { r: number; g: number; b: number }) => {
    const componentToHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    };
    return `#${componentToHex(color.r)}${componentToHex(color.g)}${componentToHex(color.b)}`;
  };

  const baseRgb = hexToRgb(baseColor);

  return {
    50: rgbToHex(adjustBrightness(baseRgb, 1.9)),
    100: rgbToHex(adjustBrightness(baseRgb, 1.7)),
    200: rgbToHex(adjustBrightness(baseRgb, 1.4)),
    300: rgbToHex(adjustBrightness(baseRgb, 1.2)),
    400: rgbToHex(adjustBrightness(baseRgb, 1.1)),
    500: baseColor, // main color
    600: rgbToHex(adjustBrightness(baseRgb, 0.9)),
    700: rgbToHex(adjustBrightness(baseRgb, 0.8)),
    800: rgbToHex(adjustBrightness(baseRgb, 0.7)),
    900: rgbToHex(adjustBrightness(baseRgb, 0.6)),
  };
};

// check if color is light or dark for contrast
const isLightColor = (hex: string) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

// generate dark mode color palette - desaturate and adjust for dark backgrounds
const generateDarkModeColorPalette = (baseColor: string) => {
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 107, g: 114, b: 128 };
  };

  const rgbToHex = (color: { r: number; g: number; b: number }) => {
    const componentToHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? `0${hex}` : hex;
    };
    return `#${componentToHex(color.r)}${componentToHex(color.g)}${componentToHex(color.b)}`;
  };

  // desaturate and lighten for better dark mode appearance
  const adjustForDarkMode = (
    color: { r: number; g: number; b: number },
    factor: number,
  ) => {
    // convert to hsl for better manipulation
    const r = color.r / 255;
    const g = color.g / 255;
    const b = color.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h: number, s: number, l: number;

    l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }

    // adjust saturation and lightness for dark mode
    s = Math.min(s * 0.7, 1); // reduce saturation by 30%
    l = Math.max(l * factor, 0.3); // ensure minimum lightness

    // convert back to rgb
    const hue2rgb = (p: number, q: number, t: number) => {
      let tNorm = t;
      if (tNorm < 0) tNorm += 1;
      if (tNorm > 1) tNorm -= 1;
      if (tNorm < 1 / 6) return p + (q - p) * 6 * tNorm;
      if (tNorm < 1 / 2) return q;
      if (tNorm < 2 / 3) return p + (q - p) * (2 / 3 - tNorm) * 6;
      return p;
    };

    let newR: number, newG: number, newB: number;

    if (s === 0) {
      newR = newG = newB = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      newR = hue2rgb(p, q, h + 1 / 3);
      newG = hue2rgb(p, q, h);
      newB = hue2rgb(p, q, h - 1 / 3);
    }

    return {
      r: Math.round(newR * 255),
      g: Math.round(newG * 255),
      b: Math.round(newB * 255),
    };
  };

  const baseRgb = hexToRgb(baseColor);

  return {
    50: rgbToHex(adjustForDarkMode(baseRgb, 2.2)),
    100: rgbToHex(adjustForDarkMode(baseRgb, 2.0)),
    200: rgbToHex(adjustForDarkMode(baseRgb, 1.8)),
    300: rgbToHex(adjustForDarkMode(baseRgb, 1.6)),
    400: rgbToHex(adjustForDarkMode(baseRgb, 1.4)),
    500: rgbToHex(adjustForDarkMode(baseRgb, 1.2)), // main color for dark mode
    600: rgbToHex(adjustForDarkMode(baseRgb, 1.0)),
    700: rgbToHex(adjustForDarkMode(baseRgb, 0.8)),
    800: rgbToHex(adjustForDarkMode(baseRgb, 0.6)),
    900: rgbToHex(adjustForDarkMode(baseRgb, 0.4)),
  };
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

// theme mode storage key
const THEME_MODE_STORAGE_KEY = "theme-mode";

// get initial theme mode from localStorage or default to light
const getInitialThemeMode = (): ThemeMode => {
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY) as ThemeMode;
  if (stored && ["light", "dark", "system"].includes(stored)) {
    return stored;
  }
  return "light";
};

export const AppThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
}) => {
  const [_brandingUpdateTrigger, setBrandingUpdateTrigger] = useState(0);
  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialThemeMode);
  const [systemPrefersDark, setSystemPrefersDark] =
    useState(getSystemPrefersDark);

  // listen for custom events when branding is updated
  useEffect(() => {
    const handleBrandingUpdate = () => {
      setBrandingUpdateTrigger((prev) => prev + 1);
    };

    window.addEventListener("tenantBrandingUpdated", handleBrandingUpdate);
    return () =>
      window.removeEventListener("tenantBrandingUpdated", handleBrandingUpdate);
  }, []);

  // listen for system theme preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () =>
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
  }, []);

  // wrap in useCallback to prevent useMemo from re-running when this function
  // is recreated on every render
  const handleSetThemeMode = useCallback((mode: ThemeMode) => {
    setThemeMode(mode);
    localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
  }, []);

  // resolve current dark mode state
  const isDark =
    themeMode === "system" ? systemPrefersDark : themeMode === "dark";

  const theme = useMemo(() => {
    let tenantPrimaryColor: string | undefined;

    /**
     * Temporarily disabled tenant branding color lookup
     *
    // always check cached branding first for immediate updates
    if (currentTenantSlug) {
      const cachedBranding = getCachedTenantBranding(currentTenantSlug);
      tenantPrimaryColor = cachedBranding?.primaryColor;
    }

    // fallback to tenant data if no cached branding
    if (!tenantPrimaryColor) {
      tenantPrimaryColor = tenantData?.branding?.primaryColor;
    }

    // if still no primary color and no tenant slug, try most recent cached tenant
    if (!tenantPrimaryColor && !currentTenantSlug) {
      const mostRecentCached = getMostRecentTenantData();
      tenantPrimaryColor = mostRecentCached?.branding?.primaryColor;
    }
    */

    const defaultColor = "#2c62b9"; // blue-500 - more visually appealing than gray

    const primaryColor = tenantPrimaryColor?.match(/^#[0-9A-Fa-f]{6}$/)
      ? tenantPrimaryColor
      : defaultColor;

    const colorPalette = isDark
      ? generateDarkModeColorPalette(primaryColor)
      : generateColorPalette(primaryColor);
    const isLight = isLightColor(isDark ? colorPalette[500] : primaryColor);

    // update CSS custom properties for non-Material-UI components
    setCSSVariables(colorPalette, primaryColor, isDark);

    return createTheme({
      palette: {
        mode: isDark ? "dark" : "light",
        primary: {
          50: colorPalette[50],
          100: colorPalette[100],
          200: colorPalette[200],
          300: colorPalette[300],
          400: colorPalette[400],
          500: colorPalette[500],
          600: colorPalette[600],
          700: colorPalette[700],
          800: colorPalette[800],
          900: colorPalette[900],
          main: isDark ? colorPalette[500] : primaryColor,
          light: colorPalette[300],
          dark: colorPalette[700],
          contrastText: isDark ? "#000000" : isLight ? "#000000" : "#ffffff",
        },
        ...(isDark && {
          background: {
            default: "#0a0a0a",
            paper: "#171717",
          },
          text: {
            primary: "#fafafa",
            secondary: "#d4d4d4",
          },
        }),
      },
      typography: {
        h5: {
          fontWeight: 700,
          letterSpacing: "0.5px",
        },
      },
      components: {
        MuiTextField: {
          styleOverrides: {
            root: {
              "& .MuiOutlinedInput-root": {
                backgroundColor: isDark ? "#171717" : "#ffffff",
                "& fieldset": {
                  borderColor: isDark ? "#404040" : "#e5e5e5",
                },
                "&:hover fieldset": {
                  borderColor: isDark ? "#525252" : "#d4d4d4",
                },
                "&.Mui-focused fieldset": {
                  borderColor: colorPalette[500],
                },
              },
              "& .MuiInputLabel-root": {
                color: isDark ? "#d4d4d4" : "#525252",
              },
              "& .MuiInputBase-input": {
                color: isDark ? "#fafafa" : "#171717",
              },
            },
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            paper: {
              backgroundColor: isDark ? "#171717" : "#ffffff",
              border: `1px solid ${isDark ? "#404040" : "#e5e5e5"}`,
            },
            option: {
              "&:hover": {
                backgroundColor: `${colorPalette[500]}25`,
              },
              "&.Mui-focused": {
                backgroundColor: `${colorPalette[500]}25`,
              },
            },
          },
        },
        ...(isDark && {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundColor: "#0a0a0a",
                color: "#f8fafc",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
        }),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark]);

  const contextValue = useMemo(
    () => ({
      theme,
      mode: themeMode,
      isDark,
      setMode: handleSetThemeMode,
    }),
    [theme, themeMode, isDark, handleSetThemeMode],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
